import React, { FunctionComponent } from 'react';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';
import { motion } from 'framer-motion';

import Flex from '@components/layout/flex';
import { easeTransition } from '@components/framer';
import Typography from '@components/basics/typography';
import CircleAddIcon from '@components/basics/icons/circle-add';
import { ConnectedAccount } from '@services/stripe-connect/types/connected-account';
import { isConnectedAccountReady } from '../../../../store/stripe-connect/selectors';

interface AddAccountItemProps {
  className?: string;
  currentAccount?: ConnectedAccount;
  redirecting: boolean;
  addressReady: boolean;
  onClick?: () => void;
}

const AddAccountItem: FunctionComponent<AddAccountItemProps> = ({
  currentAccount,
  className,
  redirecting,
  addressReady,
  onClick,
}) => {
  if (isConnectedAccountReady(currentAccount)) {
    return null;
  }

  const getButtonLabelId = () => {
    if (!addressReady) {
      return 'USER_PROFILE.WALLET.ACCOUNTS.BUTTON.ADDRESS';
    }

    if (!currentAccount) {
      return 'USER_PROFILE.WALLET.ACCOUNTS.BUTTON.CONNECT';
    }

    if (redirecting) {
      return 'USER_PROFILE.WALLET.ACCOUNTS.BUTTON.REDIRECTING';
    }

    return 'USER_PROFILE.WALLET.ACCOUNTS.BUTTON.SETUP';
  };

  const handleClick = () => {
    if (!redirecting && onClick) {
      onClick();
    }
  };

  return (
    <motion.div
      whileHover={{ y: -1 }}
      whileTap={{ scale: 1.025 }}
      initial={{ y: 1, scale: 0.8 }}
      animate={{ y: 0, scale: 1 }}
      transition={{ ...easeTransition }}
    >
      <Flex
        className={className || ''}
        justifyContent="flex-start"
        alignItems="center"
        onClick={handleClick}
        gap={10}
      >
        <CircleAddIcon />
        <Typography weight="700">
          <Translate id={getButtonLabelId()} />
        </Typography>
      </Flex>
    </motion.div>
  );
};

export default styled(AddAccountItem)`
  cursor: pointer;
  min-height: 60px;
  border-radius: 4px;
  border: 1px solid transparent;
  padding: 0 18px;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  transition: border
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover,
  &:active,
  &:focus,
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`;
